import { useMemo, useRef, type Dispatch } from 'react';
import { fitImage } from './markupGeometry';
import type { Gesture } from './markupGestures';
import type { MarkupAction } from './markupHistory';
import type { MarkupShape } from './markupShapes';
import { MarkupTextField } from './MarkupTextField';
import { useMarkupCanvas, type StageMarks } from './useMarkupCanvas';
import type { MarkupEditor } from './useMarkupEditor';
import { useMarkupHover } from './useMarkupHover';
import { useMarkupPointer } from './useMarkupPointer';
import { useMarkupTextClicks } from './useMarkupTextClicks';
import { useStageSize } from './useStageSize';

interface MarkupStageProps {
    image: ImageBitmap;
    imageLabel: string;
    shapes: readonly MarkupShape[];
    editor: MarkupEditor;
    dispatch: Dispatch<MarkupAction>;
}

function MarkupStage({ image, imageLabel, shapes, editor, dispatch }: MarkupStageProps) {
    const { tool, color, size, selected, select, text } = editor;
    const { ref: stageRef, size: stageSize } = useStageSize<HTMLDivElement>();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const gestureRef = useRef<Gesture | null>(null);
    const imageSize = useMemo(() => ({ width: image.width, height: image.height }), [image]);
    const viewport = useMemo(() => fitImage(imageSize, stageSize), [imageSize, stageSize]);
    const scale = viewport.scale;

    const hover = useMarkupHover({ tool, shapes, selected, scale });
    const hiddenId = text.draft?.editing ? text.draft.id : null;
    const marks = useMemo<StageMarks>(
        () => ({ shapes, selectedId: selected?.id ?? null, hoverId: hover.hoverId, hiddenId }),
        [shapes, selected, hover.hoverId, hiddenId]
    );
    const scheduleRedraw = useMarkupCanvas({ canvasRef, image, viewport, marks, gestureRef });
    const textClicks = useMarkupTextClicks({
        tool,
        color,
        size,
        image: imageSize,
        scale,
        shapes,
        text,
        onSelect: select
    });
    const pointer = useMarkupPointer({
        tool,
        color,
        size,
        image: imageSize,
        scale,
        shapes,
        selected,
        gestureRef,
        scheduleRedraw,
        dispatch,
        onSelect: select,
        onPress: textClicks.onPress,
        onHover: hover.track
    });

    return (
        <div className="relative flex-1 min-h-0 min-w-0 bg-gray-100">
            <div ref={stageRef} className="absolute inset-4 flex items-center justify-center">
                {scale > 0 && (
                    <div
                        className="relative shadow-lg ring-1 ring-black/5"
                        style={{ width: viewport.width, height: viewport.height }}
                    >
                        <canvas
                            ref={canvasRef}
                            role="img"
                            aria-label={`${imageLabel}, with your marks`}
                            className="block w-full h-full touch-none select-none"
                            style={{ cursor: hover.cursor }}
                            onClick={textClicks.onClick}
                            onDoubleClick={textClicks.onDoubleClick}
                            {...pointer}
                        />
                        {text.draft && (
                            <MarkupTextField
                                key={text.draft.id}
                                draft={text.draft}
                                scale={scale}
                                onChange={text.change}
                                onCommit={text.commit}
                                onCancel={text.cancel}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export { MarkupStage };
